import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Info } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import './FlightMap.css';
import DarkBasemap from '@/components/maps/DarkBasemap';
import { FLIGHT_MAP_COLORS, flightMapStyle } from '@/utils/flightMapStyle';
import { planeOnRoute, unwrapLongitudes } from '@/utils/flightRoute';

/**
 * The flight on a map: the route still ahead (dashed), the part flown so far
 * as a lit line, both airports (the one it's flying to pulses) and the plane,
 * on the app's dark OpenFreeMap basemap recoloured for this map
 * (src/utils/flightMapStyle.js). Route and position come from flight
 * tracking (server/flight-insights.js describePosition). The map is a
 * picture, not a tool: no dragging or zooming, so it never fights the trip
 * page's scrolling on a phone.
 */

// A small top-down airliner pointing north, centred on (12, 12).
const PLANE = 'M12 1.5 13.3 8.6 21.5 13.1v1.6l-8.2-2.1-.5 5.7 2.6 2v1.3L12 20.4l-3.4 1.2v-1.3l2.6-2-.5-5.7-8.2 2.1v-1.6l8.2-4.5z';

// How long the map's credit shows before it folds into an (i).
const CREDIT_FOLD_MS = 5000;

const AHEAD = { color: '#7dd3fc', opacity: 0.45, weight: 1.5, dashArray: '2 6', lineCap: 'round' };
// The flown part is three lines on top of each other, a wide faint glow, a
// brighter halo and a thin near-white core, so it reads as lit.
const FLOWN = [
  { color: FLIGHT_MAP_COLORS.coast, opacity: 0.16, weight: 10 },
  { color: FLIGHT_MAP_COLORS.coast, opacity: 0.4, weight: 4.5 },
  { color: '#f0f9ff', opacity: 0.95, weight: 1.75 },
].map((line) => ({ ...line, lineCap: 'round', lineJoin: 'round' }));

function planeIcon(heading) {
  return L.divIcon({
    className: 'flight-map-plane',
    html: `<div class="flight-map-plane-body" style="transform:rotate(${Math.round(heading || 0)}deg)">
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="${PLANE}" fill="#f0f9ff"/></svg></div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function airportIcon(code, { arriving = false } = {}) {
  return L.divIcon({
    className: 'flight-map-airport',
    html: `<div class="flight-map-airport-pin${arriving ? ' is-arriving' : ''}">
      <span class="flight-map-airport-dot"></span>
      <span class="flight-map-airport-code">${String(code || '').replace(/[^A-Z0-9]/gi, '')}</span>
    </div>`,
    iconSize: [44, 30],
    iconAnchor: [22, 5],
  });
}

/** Frame the whole route once the map knows its size. */
function FitRoute({ route }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(L.latLngBounds(route), { padding: [28, 28] });
  }, [map, route]);
  return null;
}

/**
 * Leaflet's credit line, which the basemap fills with the style's own credit
 * (OpenFreeMap, OpenMapTiles, OpenStreetMap), shown as a small dark label
 * that folds away CREDIT_FOLD_MS after it is on screen; the (i) beside the
 * map opens it again. OpenStreetMap's attribution guidelines allow exactly
 * this: shown when the map loads, collapsed after five seconds, still
 * findable behind an (i) (osmfoundation.org/wiki/Licence/Attribution_Guidelines).
 */
function MapCredit({ open, onFold }) {
  const map = useMap();

  useEffect(() => {
    // Leaflet's own link and flag; Leaflet's licence doesn't ask for it.
    map.attributionControl?.setPrefix(false);
  }, [map]);

  useEffect(() => {
    const el = map.attributionControl?.getContainer();
    if (!el) return undefined;
    el.classList.toggle('is-folded', !open);
    if (!open) return undefined;

    // The five seconds count from when the credit is on screen: the style
    // fills it in a moment after the map mounts, and the map can be below
    // the fold of the trip page.
    let visible = typeof IntersectionObserver !== 'function';
    let timer = null;
    const arm = () => {
      if (!timer && visible && el.textContent.trim()) timer = setTimeout(onFold, CREDIT_FOLD_MS);
    };
    const filled = new MutationObserver(arm);
    filled.observe(el, { childList: true, characterData: true, subtree: true });
    const seen = visible ? null : new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      arm();
    });
    seen?.observe(el);
    arm();

    // A tap on the label (not one of its links) folds it.
    const onClick = (e) => {
      if (!e.target.closest('a')) onFold();
    };
    el.addEventListener('click', onClick);
    return () => {
      clearTimeout(timer);
      filled.disconnect();
      seen?.disconnect();
      el.removeEventListener('click', onClick);
    };
  }, [map, open, onFold]);

  return null;
}

export default function FlightMap({ position, status, origin, destination, nowMs = Date.now() }) {
  const [creditOpen, setCreditOpen] = useState(true);
  const foldCredit = useCallback(() => setCreditOpen(false), []);
  const route = useMemo(() => (Array.isArray(position?.route) && position.route.length >= 2 ? unwrapLongitudes(position.route) : null), [position]);
  const plane = useMemo(() => (route ? planeOnRoute(route, position, status, nowMs) : null), [route, position, status, nowMs]);
  if (!route || !plane) return null;
  const flown = [...route.slice(0, plane.after + 1), plane.point];
  const ahead = [plane.point, ...route.slice(plane.after + 1)];

  return (
    <figure
      className="relative h-44 w-full overflow-hidden rounded-xl border border-sky-400/10 shadow-[0_0_28px_-14px_rgba(56,189,248,0.55)] sm:h-56"
      aria-label={`Map of the flight from ${origin} to ${destination}, with the plane's position`}
    >
      <MapContainer
        className="flight-map"
        bounds={L.latLngBounds(route)}
        style={{ height: '100%', width: '100%', zIndex: 0, background: FLIGHT_MAP_COLORS.sea }}
        zoomControl={false}
        dragging={false}
        touchZoom={false}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        boxZoom={false}
        keyboard={false}
        attributionControl
      >
        <DarkBasemap restyle={flightMapStyle} />
        <FitRoute route={route} />
        <MapCredit open={creditOpen} onFold={foldCredit} />
        <Polyline positions={ahead} pathOptions={AHEAD} interactive={false} />
        {FLOWN.map((line) => (
          <Polyline key={line.weight} positions={flown} pathOptions={line} interactive={false} />
        ))}
        <Marker position={route[0]} icon={airportIcon(origin)} interactive={false} keyboard={false} />
        <Marker position={route[route.length - 1]} icon={airportIcon(destination, { arriving: true })} interactive={false} keyboard={false} />
        <Marker position={plane.point} icon={planeIcon(plane.heading)} interactive={false} keyboard={false} zIndexOffset={1000} />
      </MapContainer>
      {!creditOpen && (
        <button
          type="button"
          onClick={() => setCreditOpen(true)}
          aria-label="Map credits"
          className="absolute bottom-0.5 right-0.5 z-[1000] flex h-6 w-6 items-center justify-center rounded-full text-white/40 transition-colors hover:text-white/70"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </figure>
  );
}

FlightMap.propTypes = {
  position: PropTypes.shape({
    route: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    heading: PropTypes.number,
  }),
  status: PropTypes.object,
  origin: PropTypes.string,
  destination: PropTypes.string,
  nowMs: PropTypes.number,
};

FitRoute.propTypes = { route: PropTypes.array };

MapCredit.propTypes = {
  open: PropTypes.bool.isRequired,
  onFold: PropTypes.func.isRequired,
};
